import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Shield, RefreshCw, Search, FileText, X,
  Calendar, User, MapPin, Monitor, Eye, Download, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOCUMENT_TYPES = [
  { value: '', label: 'All Documents' },
  { value: 'parental_consent', label: 'Parental Consent' },
  { value: 'ferpa_acknowledgment', label: 'FERPA Acknowledgment' },
  { value: 'ai_processing_disclosure', label: 'AI Processing Disclosure' },
  { value: 'terms_acceptance', label: 'Terms of Service' },
];

const DOC_TYPE_COLOR = {
  parental_consent: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  ferpa_acknowledgment: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  ai_processing_disclosure: 'bg-violet-500/20 text-violet-300 border-violet-500/30',
  terms_acceptance: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
};

function ConsentDetailDrawer({ record, onClose }) {
  if (!record) return null;

  const fieldRows = [
    ['Document Type', record.document_type],
    ['Document Title', record.document_title],
    ['Document Version', record.document_version || '1.0'],
    ['Signed At', record.signed_at ? new Date(record.signed_at).toLocaleString() : '-'],
    ['Parent Typed Name', record.parent_typed_name || '-'],
    ['Parent User ID', record.parent_user_id || '-'],
    ['Child Name', record.child_name || '-'],
    ['Child DOB', record.child_dob || '-'],
    ['Student User ID', record.student_user_id || '-'],
    ['Tenant', record.tenant_id || 'default'],
    ['IP Address', record.ip_address || '-'],
    ['User Agent', record.user_agent || '-'],
    ['Record ID', record.id],
  ];

  const snapshot = record.document_snapshot || record.document_content || record.content;

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0a1729]/70 backdrop-blur-sm flex items-start justify-end"
      onClick={onClose}
      data-testid="consent-detail-drawer"
    >
      <div
        className="w-full max-w-2xl h-full bg-[#0d1b30] border-l border-slate-800 overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#0d1b30] border-b border-slate-800 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-600/30 border border-emerald-500/40 flex items-center justify-center">
              <FileText className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Signed Document</h2>
              <p className="text-xs text-slate-300">Audit record · legally binding signature</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-300 hover:text-white" data-testid="close-drawer-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Document type badge */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs px-3 py-1.5 rounded-full border ${DOC_TYPE_COLOR[record.document_type] || 'bg-slate-700 text-slate-300 border-slate-600'}`}>
              {record.document_title || record.document_type}
            </span>
            <span className="text-xs text-slate-300">v{record.document_version || '1.0'}</span>
          </div>

          {/* Signature block */}
          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-emerald-400 mb-3 flex items-center gap-2">
              <Shield className="w-4 h-4" /> Signature Block
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-300">Typed signature</span>
                <span className="text-white font-medium font-serif italic">{record.parent_typed_name || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-300">Signed at</span>
                <span className="text-white">{record.signed_at ? new Date(record.signed_at).toLocaleString() : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-300">IP address</span>
                <span className="text-white font-mono text-xs">{record.ip_address || '-'}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-300 shrink-0">User agent</span>
                <span className="text-white text-xs text-right break-all">{record.user_agent || '-'}</span>
              </div>
            </div>
          </div>

          {/* Parties */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
              <p className="text-xs text-slate-300 uppercase tracking-wider mb-2">Signed By (Parent)</p>
              <p className="text-white font-medium">{record.parent_typed_name || '-'}</p>
              <p className="text-xs text-slate-300 mt-1 font-mono">{record.parent_user_id}</p>
            </div>
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
              <p className="text-xs text-slate-300 uppercase tracking-wider mb-2">Subject (Child)</p>
              <p className="text-white font-medium">{record.child_name || '-'}</p>
              <p className="text-xs text-slate-300 mt-1">DOB: {record.child_dob || '-'}</p>
              {record.student_user_id && <p className="text-xs text-slate-300 mt-1 font-mono">{record.student_user_id}</p>}
            </div>
          </div>

          {/* All fields */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-800">
              <h3 className="text-sm font-semibold text-slate-300">All Fields</h3>
            </div>
            <div className="divide-y divide-slate-800">
              {fieldRows.map(([label, value]) => (
                <div key={label} className="flex items-start justify-between gap-4 px-4 py-2.5">
                  <span className="text-xs text-slate-300 shrink-0">{label}</span>
                  <span className="text-xs text-slate-200 text-right break-all">{String(value || '-')}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Full document snapshot if stored */}
          {snapshot && (
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-300">Document Content (as signed)</h3>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const blob = new Blob([typeof snapshot === 'string' ? snapshot : JSON.stringify(snapshot, null, 2)], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `consent_${record.id}.txt`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  data-testid="download-snapshot-btn"
                >
                  <Download className="w-4 h-4 mr-2" /> Download
                </Button>
              </div>
              <pre className="p-4 text-xs text-slate-300 whitespace-pre-wrap leading-relaxed max-h-96 overflow-y-auto">
                {typeof snapshot === 'string' ? snapshot : JSON.stringify(snapshot, null, 2)}
              </pre>
            </div>
          )}

          {!snapshot && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-sm text-amber-300">
              <p className="font-medium mb-1">No document snapshot stored for this record.</p>
              <p className="text-xs text-amber-400/80">This record was signed before snapshot storage was enabled, or the document template is referenced by version only. See document template at <code className="font-mono">/api/compliance/document-template/{record.document_type}</code> for the content as of version {record.document_version || '1.0'}.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminConsentAuditPage() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [byType, setByType] = useState({});
  const [loading, setLoading] = useState(true);
  const [docType, setDocType] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (docType) params.append('document_type', docType);
      if (search) params.append('search', search);
      params.append('limit', '200');
      const res = await axios.get(`${API}/admin/consent-audit?${params.toString()}`, getAuthHeader());
      setRecords(res.data?.records || []);
      setTotal(res.data?.total || 0);
      setByType(res.data?.by_document_type || {});
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Unable to load consent audit');
      }
    } finally {
      setLoading(false);
    }
  }, [docType, search, getAuthHeader, navigate]);

  useEffect(() => {
    if (user?.role && !['super_admin', 'admin', 'org_admin'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    fetchRecords();
  }, [user, fetchRecords, navigate]);

  const openDetail = async (record) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/admin/consent-audit/${record.id}`, getAuthHeader());
      setSelected(res.data);
    } catch (err) {
      toast.error('Unable to load full document');
      setSelected(record); // fall back to list-row data
    } finally {
      setDetailLoading(false);
    }
  };

  const exportCsv = () => {
    if (!records.length) return;
    const headers = ['id', 'document_type', 'document_title', 'parent_typed_name', 'child_name', 'child_dob', 'signed_at', 'ip_address', 'parent_user_id', 'student_user_id'];
    const rows = records.map((r) => headers.map((h) => `"${String(r[h] ?? '').replace(/"/g, '""')}"`).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `consent_audit_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };

  return (
    <div className="min-h-screen bg-[#0a1628] text-white" data-testid="admin-consent-audit-page">
      {/* Header */}
      <div className="border-b border-slate-800 bg-[#0d1b30]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="text-slate-300 hover:text-white" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-600/30 border border-emerald-500/40 flex items-center justify-center">
                <FileText className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h1 className="text-lg font-semibold">Consent Audit Log</h1>
                <p className="text-xs text-slate-300">Legally-binding signatures · FERPA · COPPA · ToS</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button size="sm" variant="outline" onClick={exportCsv} disabled={!records.length} data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
            <Button size="sm" variant="outline" onClick={fetchRecords} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-gradient-to-br from-slate-700/30 to-slate-800/20 border border-slate-600/30 rounded-xl p-4">
            <p className="text-xs text-slate-300 uppercase tracking-wider mb-1">Total Records</p>
            <p className="text-2xl font-bold text-white">{total}</p>
          </div>
          {DOCUMENT_TYPES.slice(1).map((t) => (
            <div key={t.value} className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
              <p className="text-xs text-slate-300 mb-1 truncate">{t.label}</p>
              <p className="text-xl font-bold text-white">{byType[t.value] || 0}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 flex flex-col md:flex-row gap-3">
          <div className="flex items-center gap-2 flex-1">
            <Search className="w-4 h-4 text-slate-300" />
            <Input
              placeholder="Search by parent name, child name, or document title..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchRecords()}
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-300"
              data-testid="consent-search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-300" />
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="bg-slate-800 border border-slate-700 text-white rounded-md px-3 py-2 text-sm"
              data-testid="consent-doc-type-filter"
            >
              {DOCUMENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <Button size="sm" onClick={fetchRecords} data-testid="apply-filters-btn">Apply</Button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-12 flex items-center justify-center">
              <RefreshCw className="w-6 h-6 text-blue-400 animate-spin" />
            </div>
          ) : records.length === 0 ? (
            <div className="p-12 text-center text-slate-300">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No consent records match your filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-800/50 border-b border-slate-800">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-300 uppercase tracking-wider">Document</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-300 uppercase tracking-wider">Signed By</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-300 uppercase tracking-wider">Child</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-300 uppercase tracking-wider">Signed At</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-300 uppercase tracking-wider">IP</th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-slate-300 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {records.map((r) => (
                    <tr key={r.id} className="hover:bg-slate-800/30 transition-colors" data-testid={`consent-row-${r.id}`}>
                      <td className="py-3 px-4">
                        <span className={`text-xs px-2 py-1 rounded-full border ${DOC_TYPE_COLOR[r.document_type] || 'bg-slate-700 text-slate-300 border-slate-600'}`}>
                          {r.document_title || r.document_type}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-200">{r.parent_typed_name || '-'}</td>
                      <td className="py-3 px-4 text-slate-200">{r.child_name || '-'}</td>
                      <td className="py-3 px-4 text-slate-300 text-xs whitespace-nowrap">
                        {r.signed_at ? new Date(r.signed_at).toLocaleString() : '-'}
                      </td>
                      <td className="py-3 px-4 text-slate-300 text-xs font-mono">{r.ip_address || '-'}</td>
                      <td className="py-3 px-4 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDetail(r)}
                          disabled={detailLoading}
                          data-testid={`view-document-${r.id}-btn`}
                        >
                          <Eye className="w-4 h-4 mr-1" /> View Full Document
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-xs text-slate-300 text-center">
          Showing {records.length} of {total} records · Data source: <code className="font-mono">db.consent_audit_log</code>
        </p>
      </div>

      {/* Detail drawer */}
      <ConsentDetailDrawer record={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
